import { registerServiceWorker } from "./serviceWorkerRegistration";
import "./Notification.css";
import "./styles.css";
import "./fonts.css";
import "./colors.css";
import "./globals.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { _queryClient } from "./lib/client";
import mixpanel from "mixpanel-browser";
import { GoogleOAuthProvider } from "@react-oauth/google";
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
} from "react-router-dom";
import {
  Login,
  Signup,
  PrivacyPolicy,
  Profile,
  ResetPassword,
  TermsOfService,
  EditProfile,
  ChangePassword,
  OurMission,
  Widget,
  ForensicShadowAudit,
} from "./pages";
// Removed leasing ghost imports
import VendorApplyPage from "./pages/public/VendorApplyPage";
import VendorPriorityPage from "./pages/public/VendorPriorityPage";
import VendorMagicBidPage from "./pages/public/VendorMagicBidPage";
// Removed more leasing ghost imports
import ProtectedRoute from "./components/ProtectedRoute";
import { ForgotPassword, SignUp } from "./components/login";
import { GOOGLE_OAUTH_CLIENT_ID } from "./helpers/authentication";
import AuthenticationRoute from "./components/AuthenticationRoute";
import RouterErrorBoundary from "./views/RouterErrorBoundary";
import Home from "./pages/home/Home";
import Press from "./pages/Press";
import About from "./pages/About";
import ForOperators from "./pages/ForOperators";
import Contact from "./views/Contact";
import OnboardingSurveyPage from "./pages/OnboardingSurveyPage";
import CRMDashboard from "./pages/crm/CRMDashboard";
import AIAutomationDashboard from "./pages/AIAutomationDashboard";
import Offers from "./pages/Offers";
import Agents from "./pages/Agents";
import Referrals from "./pages/settings/Referrals";
import { APP_TERMS_ROUTE } from "./helpers/routeNames";
import MainLayout from "./views/MainLayout";
import NotFound from "./views/NotFound";
import Unsubscribe from "./views/Unsubscribe";
import Clients from "./pages/Clients";
import PurchaseSuccess from "./views/PurchaseSuccess";
import PurchaseCancelled from "./views/PurchaseCancelled";
// Removed Leases import
import BiddingDashboard from "./pages/BiddingDashboard";
import VendorChatDashboard from "./pages/VendorChatDashboard";
import NotificationSettings from "./pages/NotificationSettings";
import AccessDenied from "./pages/AccessDenied";
import TokenExpired from "./pages/TokenExpired";
import VendorBidPage from "./pages/VendorBidPage";
import VendorBriefingPortal from "./pages/VendorBriefingPortal";
import VendorBriefingSuccess from "./pages/VendorBriefingSuccess";
import VendorNegotiationPage from "./pages/public/VendorNegotiationPage";
import VendorBidSuccess from "./pages/VendorBidSuccess";
import VendorConversationPage from "./pages/VendorConversationPage";
import LegacyRepairTicketRoute from "./pages/LegacyRepairTicketRoute";
import BidComparisonScreenshotPage from "./pages/BidComparisonScreenshotPage";
// Removed BackgroundCheck ghost imports (deleted during purge)
// Removed TaxPrep, ClosingVault, Expenses, DealAnalyzer ghost imports (deleted during purge)
import QuickActionPage from "./pages/QuickActionPage";
import SetupVendorPage from "./pages/SetupVendorPage";
import DemoPage from "./app/demo/page";
import PreflightPage from "./pages/PreflightPage";
import CeoPulsePage from "./pages/CeoPulsePage";
import WhatsWrongPage from "./pages/WhatsWrongPage";
import V2SimulationScreen from "./pages/v2/V2SimulationScreen";
import V2Dashboard from "./pages/v2/V2Dashboard";
import ClumseChat from "./pages/ClumseChat";
import MarketingSandboxPage from "./pages/debug/MarketingSandboxPage";
import OpsDashboardEnhanced from "./pages/OpsDashboardEnhanced";
import MissionControlSurgical from "./pages/MissionControlSurgical";
import { OpsToAdminRedirect, AdminDashboardRedirect } from "./pages/admin/adminRedirects";
import CostLandingPage from "./pages/seo/CostLandingPage";
import VendorLandingPage from "./pages/seo/VendorLandingPage";

const MIXPANEL_TOKEN = (process.env.REACT_APP_MIXPANEL_TOKEN || "").trim();

if (MIXPANEL_TOKEN) {
  mixpanel.init(MIXPANEL_TOKEN, {
    debug: process.env.NODE_ENV === "development",
    track_pageview: true,
    persistence: "localStorage",
    ignore_dnt: true,
  });
} else {
  const noop = () => {};
  mixpanel.track = noop;
  mixpanel.identify = noop;
  mixpanel.people = Object.assign(mixpanel.people || {}, {
    set: noop,
    set_once: noop,
  });
  if (process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.warn(
      "[mixpanel] REACT_APP_MIXPANEL_TOKEN is not set; analytics calls are no-ops."
    );
  }
}

const router = createBrowserRouter([
  {
    element: <MainLayout />,
    errorElement: <RouterErrorBoundary />,
    children: [
      {
        path: "/",
        element: <Home />,
      },
      {
        path: "/platform",
        element: <Navigate to="/" replace />,
      },
      {
        path: "/help",
        element: <Navigate to="/" replace />,
      },
      {
        path: "/help/*",
        element: <Navigate to="/" replace />,
      },
      // Blog routes hidden for now
      // {
      //   path: "/blog",
      //   element: <BlogIndex />,
      // },
      // {
      //   path: "/blog/:slug",
      //   element: <BlogArticle />,
      // },
      {
        path: "/about",
        element: <About />,
      },
      {
        path: "/protocols",
        element: <Navigate to="/" replace />,
      },
      {
        path: "/protocols/:slug",
        element: <Navigate to="/" replace />,
      },
      {
        path: "/glossary",
        element: <Navigate to="/" replace />,
      },
      {
        path: "/tenants",
        element: <Navigate to="/chat" replace />,
      },
      {
        path: "/for-operators",
        element: <ForOperators />,
      },
      {
        path: "/for-self-managing-landlords",
        element: <Navigate to="/for-operators" replace />,
      },
      {
        path: "/opschat",
        element: <Navigate to="/" replace />,
      },
      {
        path: "/preflight",
        element: <PreflightPage />,
      },
      {
        path: "/debug/marketing-sandbox",
        element: <MarketingSandboxPage />,
      },
      {
        path: "/ceo-pulse",
        element: <CeoPulsePage />,
      },
      {
        path: "/press",
        element: <Press />,
      },
      {
        path: "/privacy",
        element: <PrivacyPolicy />,
      },
      {
        path: "/reset-password",
        element: <ResetPassword />,
      },
      {
        path: APP_TERMS_ROUTE,
        element: <TermsOfService />,
      },
      {
        path: "/contact",
        element: <Contact />,
      },
      {
        path: "/unsubscribe",
        element: <Unsubscribe />,
      },
      {
        path: "/agents",
        element: <Agents />,
      },
      {
        path: "/forensic-audit",
        element: <ForensicShadowAudit />,
      },
      {
        path: "/purchase-success",
        element: <PurchaseSuccess />,
      },
      {
        path: "/purchase-cancelled",
        element: <PurchaseCancelled />,
      },
      // Removed rent payment routes
      // Removed tenant view routes
      {
        path: "/tenant-chat/:tenantId",
        element: <Navigate to="/" replace />,
      },
      {
        path: "/bid/:token",
        element: <VendorBidPage />,
      },
      {
        path: "/vendor/chat/:token",
        element: <VendorBidPage />,
      },
      {
        path: "/negotiate/:bidToken",
        element: <VendorNegotiationPage />,
      },
      {
        path: "/portal/:vendorId/:jobId",
        element: <VendorBriefingPortal />,
      },
      {
        path: "/vendor-portal/:vendorId/:jobId/success",
        element: <VendorBriefingSuccess />,
      },
      {
        path: "/buyersclub",
        element: <Navigate to="/" replace />,
      },
      {
        path: "/legacy",
        element: <Navigate to="/" replace />,
      },
      {
        path: "/chat/:propertySlug",
        element: <Navigate to="/" replace />,
      },
      {
        path: "/quick-action/:token",
        element: <QuickActionPage />,
      },
      {
        path: "/bid/success",
        element: <VendorBidSuccess />,
      },
      {
        path: "/conversations/:conversationId",
        element: <VendorConversationPage />,
      },
      {
        path: "/repairs/:ticketId",
        element: <LegacyRepairTicketRoute />,
      },
      {
        path: "/repairs",
        element: <Navigate to="/bids" replace />,
      },
      {
        path: "/access-denied",
        element: <AccessDenied />,
      },
      {
        path: "/token-expired",
        element: <TokenExpired />,
      },
      // Removed leasing and payment ghost routes
      {
        path: "/vendors",
        element: <Navigate to="/vendors/apply" replace />,
      },
      {
        path: "/vendors/apply",
        element: <VendorApplyPage />,
      },
      {
        path: "/vendors/priority",
        element: <VendorPriorityPage />,
      },
      // Programmatic SEO: vendor pages for each trade × neighborhood combo.
      // Must be declared AFTER the static /vendors/apply and /vendors/priority
      // routes so React Router's static-path preference keeps those working.
      {
        path: "/vendors/:trade/:neighborhoodSlug",
        element: <VendorLandingPage />,
      },
      // Programmatic SEO: cost-guide pages for each service × ZIP combo.
      {
        path: "/cost/:serviceType/:zipCode",
        element: <CostLandingPage />,
      },
      {
        path: "/j/:token",
        element: <VendorMagicBidPage />,
      },
      // Removed payment and screening ghost routes
      {
        // Public outbound-lead personalised demo; no auth required
        path: "/demo",
        element: <DemoPage />,
      },
      {
        path: "/ops-dashboard",
        element: <OpsToAdminRedirect />,
      },
      {
        // Unified admin: Mission Control + B2C performance metrics
        // /admin?auth_token=APP_SECRET (stored in session) or Firebase admin claim
        path: "/admin",
        element: <MissionControlSurgical />,
      },
      {
        path: "/admin/dashboard",
        element: <AdminDashboardRedirect />,
      },
      {
        // Legacy alias - redirects to /admin with auth_token
        path: "/ops",
        element: <OpsToAdminRedirect />,
      },
      {
        // Legacy Mission Control (original styling) - accessible via /ops-legacy
        path: "/ops-legacy",
        element: <OpsDashboardEnhanced />,
      },
      {
        // requireAccount: authenticated operators only.
        element: <ProtectedRoute requireAccount />,
        children: [
          { path: "/widget", element: <Widget /> },
          { path: "/crm", element: <CRMDashboard /> },
          { path: "/clients", element: <Clients /> },
          // Removed leasing booking routes
          {
            path: "/property-dashboard",
            element: <Navigate to="/chat" replace />,
          },
          { path: "/vendor-chat", element: <VendorChatDashboard /> },
        ],
      },
      {
        path: "/login",
        element: (
          <AuthenticationRoute>
            <Login />
          </AuthenticationRoute>
        ),
      },
      {
        path: "/signup",
        element: (
          <AuthenticationRoute>
            <Signup />
          </AuthenticationRoute>
        ),
      },
      {
        path: "/forgot-password",
        element: (
          <AuthenticationRoute>
            <ForgotPassword />
          </AuthenticationRoute>
        ),
      },
      // V2 bolt-on "AI Front Desk" routes (gated: must be logged in)
      {
        element: <ProtectedRoute requireAccount />,
        children: [
          { path: "/v2/simulation", element: <V2SimulationScreen /> },
          { path: "/v2/dashboard", element: <V2Dashboard /> },
        ],
      },
      // Paths that require a user to be logged in
      {
        element: <ProtectedRoute requireAccount />,
        children: [
          // Single route with optional params so navigating /chat ↔ /chat/:id/:slug
          // does not remount ClumseChat (preserves in-memory draft threads).
          { path: "/chat/:chatId?/:slug?", element: <ClumseChat /> },
          { path: "/dashboard", element: <Navigate to="/chat" replace /> },
          { path: "/whats-wrong", element: <WhatsWrongPage /> },
          { path: "/operations", element: <Navigate to="/chat" replace /> },
          {
            path: "/dashboard/legacy",
            element: <Navigate to="/chat" replace />,
          },
          { path: "/units", element: <Navigate to="/chat" replace /> },
          {
            path: "/communication",
            element: <Navigate to="/bids" replace />,
          },
          {
            path: "/communication/:tenantId",
            element: <Navigate to="/bids" replace />,
          },
          // Removed leases route
          {
            path: "/demo/bid-comparison",
            element: <BidComparisonScreenshotPage />,
          },
          {
            path: "/properties/:propertyId/setup-vendor",
            element: <SetupVendorPage />,
          },
          // Removed expenses route
          { path: "/bids", element: <BiddingDashboard /> },
          // Removed rent and reminder routes
          { path: "/engine", element: <AIAutomationDashboard /> },
          { path: "/clumse-ai", element: <Navigate to="/engine" replace /> },
          { path: "/offers", element: <Offers /> },
          // Removed accounting and leasing ghost routes
          { path: "/referrals", element: <Referrals /> },
          {
            path: "/profile",
            element: <Profile />,
          },
          { path: "/profile/edit", element: <EditProfile /> },
          { path: "/profile/change-password", element: <ChangePassword /> },
          {
            path: "/survey/first-impressions",
            element: <OnboardingSurveyPage />,
          },
          // { path: "/visualizer", element: <Visualizer /> },
        ],
      },
      {
        path: "/notification-settings",
        element: (
          <ProtectedRoute>
            <NotificationSettings />
          </ProtectedRoute>
        ),
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
]);

registerServiceWorker();

ReactDOM.createRoot(document.getElementById("root")).render(
  <>
    <GoogleOAuthProvider clientId={GOOGLE_OAUTH_CLIENT_ID}>
      <QueryClientProvider client={_queryClient}>
        <RouterProvider router={router} />
        {/* <ReactQueryDevtools buttonPosition="bottom-left" /> */}
      </QueryClientProvider>
    </GoogleOAuthProvider>
  </>
);
